import { z } from 'zod';
import { Id, Role } from '../primitives.js';
import { Password, TenantKind } from './identity.js';

/**
 * Приглашение сотрудника и всё, что связано с заведением людей в клинике.
 *
 * Ключевое решение здесь одно: **секрет уходит в ответе, а не в письме**.
 * Сервиса уведомлений в фазе 1 нет, и ссылку передаёт человек человеку —
 * в мессенджере, на бумаге, вслух по телефону. Это не временный костыль:
 * администратор клиники всё равно звонит новому сотруднику и объясняет, что
 * делать, а письмо, ушедшее на адрес с опечаткой, приглашает в клинику
 * незнакомца.
 *
 * В базе лежит только SHA-256 от секрета, поэтому показать ссылку второй раз
 * невозможно: потерянное приглашение не восстанавливается, а выпускается
 * заново. Так же устроен и сброс пароля.
 *
 * Документация модуля: docs/03-modules/01-identity-access.md
 */

/**
 * Секрет приглашения в том виде, в котором его получает сотрудник.
 *
 * Внутри две части: идентификатор клиники и случайные 256 бит. Клиника нужна
 * не для красоты — приглашение лежит в таблице арендатора, закрытой политикой
 * изоляции, и найти строку по одному хешу невозможно, не сказав базе, в какой
 * клинике искать. Знание идентификатора клиники ничего не даёт: без второй
 * части поиск не находит ничего.
 */
export const InvitationToken = z.string().min(40).max(200);

export const InviteStaffRequest = z.object({
  email: z.email(),
  /**
   * Роль назначается сразу: приглашение без роли означало бы сотрудника,
   * который вошёл и не может ничего, а права ему выдают потом и вручную.
   */
  role: Role,
});
export type InviteStaffRequest = z.infer<typeof InviteStaffRequest>;

export const Invitation = z.object({
  id: Id,
  tenantId: Id,
  email: z.email(),
  role: Role,
  /** Кто пригласил: у приглашения в чужую клинику должен быть автор. */
  invitedById: Id,
  expiresAt: z.iso.datetime(),
  createdAt: z.iso.datetime(),
});
export type Invitation = z.infer<typeof Invitation>;

/**
 * Ответ на создание приглашения: сама запись и секрет — единственный раз за
 * всю его жизнь.
 */
export const InvitationIssued = Invitation.extend({
  token: InvitationToken,
});
export type InvitationIssued = z.infer<typeof InvitationIssued>;

export const InvitationListResponse = z.object({
  invitations: z.array(Invitation),
});
export type InvitationListResponse = z.infer<typeof InvitationListResponse>;

/**
 * Что видит человек, открывший ссылку, до того как что-либо ввёл.
 *
 * Название клиники здесь обязательно: сотрудник должен узнать место, куда его
 * зовут, прежде чем придумывать пароль. `accountExists` определяет форму:
 * работающему в другой клинике нужен его нынешний пароль, а не второй аккаунт
 * на тот же адрес.
 */
export const InvitationPreview = z.object({
  tenantName: z.string().min(1),
  tenantKind: TenantKind,
  email: z.email(),
  role: Role,
  accountExists: z.boolean(),
  expiresAt: z.iso.datetime(),
});
export type InvitationPreview = z.infer<typeof InvitationPreview>;

/**
 * Принятие приглашения.
 *
 * Одна форма на два случая, и различает их сервер, а не клиент. Нового
 * сотрудника он заводит вместе с именем и новым паролем; человеку, который уже
 * работает в другой клинике, — добавляет место работы, проверив его нынешний
 * пароль. Адрес почты в теле не передаётся: он берётся из приглашения, иначе
 * ссылку можно было бы применить к чужой учётной записи.
 */
export const AcceptInvitationRequest = z.object({
  token: InvitationToken,
  /** У нового сотрудника — новый пароль, у работающего — его нынешний. */
  password: z.string().min(1).max(200),
  lastName: z.string().trim().min(1).max(100).optional(),
  firstName: z.string().trim().min(1).max(100).optional(),
  middleName: z.string().trim().max(100).optional(),
});
export type AcceptInvitationRequest = z.infer<typeof AcceptInvitationRequest>;

/**
 * Сотрудник, заведённый сразу, без ссылки.
 *
 * Пароль здесь задаёт не сам сотрудник, а руководитель — открывать ссылку и
 * разбираться с формой удобно не всякому, особенно если видишь такое впервые.
 * Поэтому путь работает только для человека, которого в системе ещё нет:
 * чужой пароль руками администратора не заводят. Тому, кто на этот адрес уже
 * где-то работает, выдают обычное приглашение — он сам подтвердит доступ
 * своим, а не вторым от чужих рук.
 */
export const CreateStaffMemberRequest = z.object({
  email: z.email(),
  password: Password,
  role: Role,
  lastName: z.string().trim().min(1).max(100),
  firstName: z.string().trim().min(1).max(100),
  middleName: z.string().trim().max(100).optional(),
});
export type CreateStaffMemberRequest = z.infer<typeof CreateStaffMemberRequest>;

/**
 * Смена роли — отдельная операция, а не поле карточки сотрудника.
 *
 * Она отзывает все его сессии, требует, чтобы в клинике остался владелец, и
 * попадает в журнал безопасности отдельной строкой. Смешать её с правкой
 * должности значило бы дать сменить права незаметно для того, кто смотрит
 * журнал.
 */
export const ChangeRoleRequest = z.object({
  role: Role,
});
export type ChangeRoleRequest = z.infer<typeof ChangeRoleRequest>;

/**
 * Отстранение и увольнение. `invited` сюда не входит: приглашённым нельзя
 * стать обратно, приглашение выпускается заново.
 */
export const ChangeMembershipStatusRequest = z.object({
  status: z.enum(['active', 'suspended', 'deactivated']),
});
export type ChangeMembershipStatusRequest = z.infer<typeof ChangeMembershipStatusRequest>;

/**
 * Сброс пароля, выданный руководителем: сотрудник подходит к администратору, и
 * тот выдаёт одноразовую ссылку. Рядом — самостоятельный сброс по почте
 * (`RequestPasswordResetRequest`); ссылка у обоих одна и та же.
 */
export const PasswordResetIssued = z.object({
  token: InvitationToken,
  expiresAt: z.iso.datetime(),
});
export type PasswordResetIssued = z.infer<typeof PasswordResetIssued>;

/**
 * Пароль, который руководитель задаёт сотруднику сам.
 *
 * Письма нет: пароль называют вслух или в мессенджере клиники. Самостоятельная
 * смена по почте — отдельный путь (`RequestPasswordResetRequest`), его это
 * не отменяет.
 */
export const SetStaffPasswordRequest = z.object({
  password: Password,
});
export type SetStaffPasswordRequest = z.infer<typeof SetStaffPasswordRequest>;

export const ConfirmPasswordResetRequest = z.object({
  token: InvitationToken,
  password: Password,
});
export type ConfirmPasswordResetRequest = z.infer<typeof ConfirmPasswordResetRequest>;

/**
 * Самостоятельный сброс: человек забыл пароль и просит ссылку на свою почту.
 * Ответ один и тот же, есть такой адрес или нет, — иначе форма стала бы
 * способом узнать, кто работает в клинике.
 */
export const RequestPasswordResetRequest = z.object({
  email: z.email().max(320),
});
export type RequestPasswordResetRequest = z.infer<typeof RequestPasswordResetRequest>;
